import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { IconsNgModule } from '@seech/icons-ng';
import {
  LayoutNavigationItemViewModel,
  LayoutProfileCommand,
  LayoutProfileViewModel,
} from './layout-presentation.models';

@Component({
  selector: 'app-layout-profile-menu',
  standalone: true,
  imports: [IconsNgModule],
  templateUrl: './layout-profile-menu.component.html',
  styleUrl: './layout-profile-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutProfileMenuComponent implements OnDestroy {
  readonly model = input.required<LayoutProfileViewModel>();
  readonly collapsed = input(false);
  readonly commandRequested = output<LayoutProfileCommand>();
  readonly menuOpen = signal(false);

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly document = inject(DOCUMENT);
  private readonly menuTrigger =
    viewChild<ElementRef<HTMLButtonElement>>('menuTrigger');
  private readonly menu = viewChild<ElementRef<HTMLElement>>('menu');
  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  private focusMenuOnRender = false;

  constructor() {
    effect(() => {
      const menu = this.menu();
      if (this.menuOpen() && menu && this.focusMenuOnRender) {
        menu.nativeElement.querySelector<HTMLAnchorElement>('a')?.focus();
        this.focusMenuOnRender = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.clearCloseTimer();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target;
    if (
      !this.menuOpen() ||
      (target instanceof Node && this.host.nativeElement.contains(target))
    ) {
      return;
    }

    this.closeMenu(this.menuOwnsFocus());
  }

  @HostListener('document:keydown.escape', ['$event'])
  onDocumentEscape(event: Event): void {
    if (!this.menuOpen()) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.closeMenu(true);
  }

  toggleMenu(event: Event): void {
    event.stopPropagation();
    this.clearCloseTimer();
    if (this.menuOpen()) {
      this.closeMenu(true);
      return;
    }

    this.focusMenuOnRender = true;
    this.menuOpen.set(true);
  }

  onMouseEnter(): void {
    if (!this.collapsed()) {
      return;
    }

    this.clearCloseTimer();
    this.focusMenuOnRender = false;
    this.menuOpen.set(true);
  }

  onMouseLeave(): void {
    if (!this.collapsed()) {
      return;
    }

    this.clearCloseTimer();
    this.closeTimer = setTimeout(() => {
      this.closeTimer = null;
      this.closeMenu(this.menuOwnsFocus());
    }, 150);
  }

  requestNavigation(
    event: MouseEvent,
    item: LayoutNavigationItemViewModel,
  ): void {
    if (!isUnmodifiedPrimaryActivation(event)) {
      return;
    }

    event.preventDefault();
    this.commandRequested.emit({ kind: 'navigate', route: item.route });
    this.closeMenu(true);
  }

  requestSignOut(event: MouseEvent): void {
    if (!isUnmodifiedPrimaryActivation(event)) {
      return;
    }

    event.preventDefault();
    this.commandRequested.emit({ kind: 'navigate', route: '/logout' });
    this.closeMenu(true);
  }

  private closeMenu(restoreFocus: boolean): void {
    this.clearCloseTimer();
    this.focusMenuOnRender = false;
    this.menuOpen.set(false);
    if (restoreFocus) {
      this.menuTrigger()?.nativeElement.focus();
    }
  }

  private menuOwnsFocus(): boolean {
    const menu = this.menu()?.nativeElement;
    const focusedElement = this.document.activeElement;
    return Boolean(menu && focusedElement && menu.contains(focusedElement));
  }

  private clearCloseTimer(): void {
    if (this.closeTimer !== null) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }
}

function isUnmodifiedPrimaryActivation(event: MouseEvent): boolean {
  return (
    event.button === 0 &&
    !event.altKey &&
    !event.ctrlKey &&
    !event.metaKey &&
    !event.shiftKey
  );
}
